import * as ACL from '@/domain/restriction/policies';
import { BANK_FEED_ROUTE_TABS } from '@/domain/bankFeed/constants';

export const PARAMS = {
  COMPANY_ID: 'companyId',
  DOCUMENT_ID: 'documentId',
  DOCUMENT_TYPE: 'documentType',
  CATEGORY1: 'category1',
  CATEGORY2: 'category2',
  CATEGORY3: 'category3',
  RECOVERY_TOKEN: 'recoveryToken',
  COMPANY_TYPE: 'companyType',
  SECTION: 'section',
  ACTION: 'action',
  REDIRECT_TO: 'redirectTo',
  PASSWORD_EXPIRED: 'passwordExpired',
  TAB: 'tab',
  QUERY_PARAMS: 'queryParams',
  TASK_ID: 'taskId',
  PAYMENT_ID: 'paymentId',
};

/**
 * id - string;
 * absolute - absolute path for generatePath fn;
 * relative - relative path used only for router;
 * restriction - bitmask || boolean;
 *   absent on a route whose area decides access through its CASL case;
 */

const ROUTES_PATH = {
  // ==============================
  // ======== ORGANIZATION
  ORGANIZATIONS: {
    id: 'ORGANIZATIONS',
    absolute: '/organizations',
    relative: '/organizations',
    restriction: ACL.IS_AUTHORIZED,
  },
  // ==============================
  // ======== COMPANIES
  COMPANIES: {
    id: 'COMPANIES',
    absolute: '/',
    relative: '/',
    restriction: ACL.IS_AUTHORIZED,
  },
  // ==============================
  // ======== COMPANY
  COMPANY: {
    id: 'COMPANY',
    absolute: '/company/*',
    relative: 'company/*',
  },
  COMPANY_WORKSPACE: {
    id: 'COMPANY_WORKSPACE',
    absolute: `/company/standard/:${PARAMS.COMPANY_ID}/:${PARAMS.CATEGORY1}/:${PARAMS.CATEGORY2}?/:${PARAMS.CATEGORY3}?/:${PARAMS.QUERY_PARAMS}?`,
    relative: `standard/:${PARAMS.COMPANY_ID}/:${PARAMS.CATEGORY1}/:${PARAMS.CATEGORY2}?/:${PARAMS.CATEGORY3}?/:${PARAMS.QUERY_PARAMS}?`,
  },
  COMPANY_WORKSPACE_CATEGORY_UNDEFINED: {
    id: 'COMPANY_WORKSPACE_CATEGORY_UNDEFINED',
    absolute: `/company/standard/:${PARAMS.COMPANY_ID}`,
    relative: `/company/standard/:${PARAMS.COMPANY_ID}`,
  },
  COMPANY_CONFIDENTIAL_WORKSPACE: {
    id: 'COMPANY_CONFIDENTIAL_WORKSPACE',
    absolute: `/company/confidential/:${PARAMS.COMPANY_ID}/:${PARAMS.CATEGORY1}/:${PARAMS.CATEGORY2}?/:${PARAMS.CATEGORY3}?/:${PARAMS.QUERY_PARAMS}?`,
    relative: `confidential/:${PARAMS.COMPANY_ID}/:${PARAMS.CATEGORY1}/:${PARAMS.CATEGORY2}?/:${PARAMS.CATEGORY3}?/:${PARAMS.QUERY_PARAMS}?`,
  },
  COMPANY_CONFIDENTIAL_WORKSPACE_CATEGORY_UNDEFINED: {
    id: 'COMPANY_CONFIDENTIAL_WORKSPACE_CATEGORY_UNDEFINED',
    absolute: `/company/confidential/:${PARAMS.COMPANY_ID}`,
    relative: `/company/confidential/:${PARAMS.COMPANY_ID}`,
  },
  COMPANY_DASHBOARD: {
    id: 'COMPANY_DASHBOARD',
    absolute: `/company/dashboard/:${PARAMS.COMPANY_ID}/*`,
    relative: `dashboard/:${PARAMS.COMPANY_ID}/*`,
  },
  COMPANY_DASHBOARD_VENDOR_EXPENSES: {
    id: 'COMPANY_DASHBOARD_VENDOR_EXPENSES',
    absolute: `/company/dashboard/:${PARAMS.COMPANY_ID}/vendor-expenses`,
    relative: 'vendor-expenses',
  },
  // ==============================
  // ======== BANK FEED
  COMPANY_BANK_FEED: {
    id: 'COMPANY_BANK_FEED',
    absolute: `/company/:${PARAMS.COMPANY_ID}/bank-feed/*`,
    relative: `:${PARAMS.COMPANY_ID}/bank-feed/*`,
  },
  COMPANY_BANK_FEED_TRANSACTIONS: {
    id: 'COMPANY_BANK_FEED_TRANSACTIONS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/bank-feed/${BANK_FEED_ROUTE_TABS.TRANSACTIONS}`,
    relative: BANK_FEED_ROUTE_TABS.TRANSACTIONS,
  },
  COMPANY_BANK_FEED_CREDIT_CARDS: {
    id: 'COMPANY_BANK_FEED_CREDIT_CARDS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/bank-feed/${BANK_FEED_ROUTE_TABS.CREDIT_CARDS}`,
    relative: BANK_FEED_ROUTE_TABS.CREDIT_CARDS,
  },
  // ==============================
  // ======== DOCUMENT
  DOCUMENT: {
    id: 'DOCUMENT',
    absolute: `/company/:${PARAMS.COMPANY_ID}/document/:${PARAMS.DOCUMENT_ID}`,
    relative: `/company/:${PARAMS.COMPANY_ID}/document/:${PARAMS.DOCUMENT_ID}`,
    restriction: ACL.IS_AUTHORIZED,
  },
  DOCUMENT_MOBILE_APPROVALS: {
    id: 'DOCUMENT_MOBILE_APPROVALS',
    absolute: `/mobile/approvals/:${PARAMS.COMPANY_ID}/:${PARAMS.DOCUMENT_ID}?`,
    relative: `/mobile/approvals/:${PARAMS.COMPANY_ID}/:${PARAMS.DOCUMENT_ID}?`,
    restriction: ACL.IS_AUTHORIZED,
  },
  // ==============================
  // ======== ORGANIZATION_SETTINGS
  ORGANIZATION_SETTINGS: {
    id: 'ORGANIZATION_SETTINGS',
    absolute: '/configurations/*',
    relative: 'configurations/*',
    restriction: ACL.IS_ADMIN,
  },
  ORGANIZATION_SETTINGS_GENERAL: {
    id: 'ORGANIZATION_SETTINGS_GENERAL',
    absolute: '/configurations/general',
    relative: 'general',
    restriction: ACL.IS_ADMIN,
  },
  ORGANIZATION_SETTINGS_GENERAL_MAIN: {
    id: 'ORGANIZATION_SETTINGS_GENERAL_MAIN',
    absolute: '/configurations/general/main',
    relative: 'main',
    restriction: ACL.IS_ADMIN,
  },
  ORGANIZATION_SETTINGS_GENERAL_CONTACT: {
    id: 'ORGANIZATION_SETTINGS_GENERAL_CONTACT',
    absolute: '/configurations/general/contact',
    relative: 'contact',
    restriction: ACL.IS_ADMIN,
  },
  ORGANIZATION_SETTINGS_GENERAL_FEATURES: {
    id: 'ORGANIZATION_SETTINGS_GENERAL_FEATURES',
    absolute: '/configurations/general/features',
    relative: 'features',
    restriction: ACL.IS_ADMIN,
  },
  ORGANIZATION_SETTINGS_BILLINGS: {
    id: 'ORGANIZATION_SETTINGS_BILLINGS',
    absolute: '/configurations/billings',
    relative: 'billings',
    restriction: ACL.IS_ADMIN,
  },
  ORGANIZATION_SETTINGS_USERS: {
    id: 'ORGANIZATION_SETTINGS_USERS',
    absolute: '/configurations/users',
    relative: 'users',
    restriction: ACL.IS_ADMIN,
  },
  ORGANIZATION_SETTINGS_SECURITY: {
    id: 'ORGANIZATION_SETTINGS_SECURITY',
    absolute: '/configurations/security',
    relative: 'security',
    restriction: ACL.IS_ADMIN,
  },
  ORGANIZATION_SETTINGS_BACKUPS: {
    id: 'ORGANIZATION_SETTINGS_BACKUPS',
    absolute: '/configurations/backups',
    relative: 'backups',
    restriction: ACL.IS_ADMIN,
  },
  ORGANIZATION_SETTINGS_API_KEYS: {
    id: 'ORGANIZATION_SETTINGS_API_KEYS',
    absolute: '/configurations/keys',
    relative: 'keys',
    restriction: ACL.IS_ADMIN,
  },

  // ==============================
  // ======== COMPANY_SETTINGS
  COMPANY_SETTINGS: {
    id: 'COMPANY_SETTINGS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/*`,
    relative: `/company/:${PARAMS.COMPANY_ID}/configurations/*`,
  },
  COMPANY_SETTINGS_GENERAL: {
    id: 'COMPANY_SETTINGS_GENERAL',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/general`,
    relative: 'general',
  },
  COMPANY_SETTINGS_GENERAL_MAIN: {
    id: 'COMPANY_SETTINGS_GENERAL_MAIN',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/general/main`,
    relative: 'main',
  },
  COMPANY_SETTINGS_GENERAL_FEATURES: {
    id: 'COMPANY_SETTINGS_GENERAL_FEATURES',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/general/features`,
    relative: 'features',
  },
  COMPANY_SETTINGS_USERS: {
    id: 'COMPANY_SETTINGS_USERS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/users`,
    relative: 'users',
  },
  COMPANY_SETTINGS_INTEGRATIONS: {
    id: 'COMPANY_SETTINGS_INTEGRATIONS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/integrations`,
    relative: 'integrations',
  },
  COMPANY_SETTINGS_APPLICATIONS: {
    id: 'COMPANY_SETTINGS_APPLICATIONS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/applications`,
    relative: 'applications',
  },
  COMPANY_SETTINGS_VENDOR_PREFERENCES: {
    id: 'COMPANY_SETTINGS_VENDOR_PREFERENCES',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/supplier_defaults`,
    relative: 'supplier_defaults',
  },
  COMPANY_SETTINGS_VENDOR_PREFERENCES_GENERAL: {
    id: 'COMPANY_SETTINGS_VENDOR_PREFERENCES_GENERAL',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/supplier_defaults/general_management`,
    relative: 'general_management',
  },
  COMPANY_SETTINGS_VENDOR_PREFERENCES_POLICIES: {
    id: 'COMPANY_SETTINGS_VENDOR_PREFERENCES_POLICIES',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/supplier_defaults/policy_management`,
    relative: 'policy_management',
  },
  COMPANY_SETTINGS_APPROVALS: {
    id: 'COMPANY_SETTINGS_APPROVALS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/approvals`,
    relative: 'approvals',
  },
  COMPANY_SETTINGS_APPROVALS_APPROVERS: {
    id: 'COMPANY_SETTINGS_APPROVALS_APPROVERS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/approvals/approvers`,
    relative: 'approvers',
  },
  COMPANY_SETTINGS_APPROVALS_BASIC_GROUPS: {
    id: 'COMPANY_SETTINGS_APPROVALS_BASIC_GROUPS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/approvals/groups`,
    relative: 'groups',
  },
  COMPANY_SETTINGS_APPROVALS_ADVANCED_GROUPS: {
    id: 'COMPANY_SETTINGS_APPROVALS_ADVANCED_GROUPS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/approvals/advanced_groups`,
    relative: 'advanced_groups',
  },
  COMPANY_SETTINGS_APPROVALS_VENDOR_BASED: {
    id: 'COMPANY_SETTINGS_APPROVALS_VENDOR_BASED',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/approvals/vendor_based_approval`,
    relative: 'vendor_based_approval',
  },
  COMPANY_SETTINGS_APPROVALS_FEATURES: {
    id: 'COMPANY_SETTINGS_APPROVALS_FEATURES',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/approvals/features`,
    relative: 'features',
  },
  COMPANY_SETTINGS_BANK_FEED: {
    id: 'COMPANY_SETTINGS_BANK_FEED',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/bank-feed`,
    relative: 'bank-feed',
  },
  COMPANY_SETTINGS_BANK_FEED_CONNECTIONS: {
    id: 'COMPANY_SETTINGS_BANK_FEED_CONNECTIONS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/bank-feed/connections`,
    relative: 'connections',
  },
  COMPANY_SETTINGS_BANK_FEED_USERS_AND_PERMISSIONS: {
    id: 'COMPANY_SETTINGS_BANK_FEED_USERS_AND_PERMISSIONS',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/bank-feed/users`,
    relative: 'users',
  },
  COMPANY_SETTINGS_BANK_FEED_SYNCHRONIZATION: {
    id: 'COMPANY_SETTINGS_BANK_FEED_SYNCHRONIZATION',
    absolute: `/company/:${PARAMS.COMPANY_ID}/configurations/bank-feed/sync`,
    relative: 'sync',
  },

  // ==============================
  // ======== PROFILE_SETTINGS
  PROFILE_SETTINGS: {
    id: 'PROFILE_SETTINGS',
    absolute: '/profile/*',
    relative: '/profile/*',
    restriction: ACL.IS_AUTHORIZED,
  },
  PROFILE_SETTINGS_MAIN: {
    id: 'PROFILE_SETTINGS_MAIN',
    absolute: '/profile/main',
    relative: 'main',
    restriction: ACL.IS_AUTHORIZED,
  },
  PROFILE_SETTINGS_NOTIFICATIONS: {
    id: 'PROFILE_SETTINGS_NOTIFICATIONS',
    absolute: '/profile/notifications',
    relative: 'notifications',
    restriction: ACL.IS_AUTHORIZED,
  },
  PROFILE_SETTINGS_PREFERENCES: {
    id: 'PROFILE_SETTINGS_PREFERENCES',
    absolute: '/profile/preferences',
    relative: 'preferences',
    restriction: ACL.IS_AUTHORIZED,
  },

  // ==============================
  // ======== AUTH
  AUTH_LOGIN: {
    id: 'AUTH_LOGIN',
    absolute: '/login',
    relative: '/login',
    // in case user is authorized, he is redirected to /
    // in case not, we must not consider this route allowed, otherwise
    // user will remain on it instead of being redirected to login app
    // @see src/sagas/auth.js#109 at this commit tree
    restriction: false,
  },
  AUTH_IDP_LOGIN: {
    id: 'AUTH_IDP_LOGIN',
    absolute: '/idp',
    relative: '/idp',
    restriction: true,
  },

  // ==============================
  // ======== PAY MODULE

  PAY_COMPANY: {
    id: 'PAY_COMPANY',
    absolute: `/pay/company/:${PARAMS.COMPANY_ID}/payments/:${PARAMS.CATEGORY1}?`,
    relative: `/pay/company/:${PARAMS.COMPANY_ID}/payments/:${PARAMS.CATEGORY1}?`,
    restriction: ACL.IS_PAY_MODULE_AVAILABLE_FOR_THIS_COMPANY,
  },

  PAY_PAYMENT: {
    id: 'PAY_PAYMENT',
    absolute: `/pay/company/:${PARAMS.COMPANY_ID}/payment/:${PARAMS.PAYMENT_ID}`,
    relative: `/pay/company/:${PARAMS.COMPANY_ID}/payment/:${PARAMS.PAYMENT_ID}`,
    restriction: ACL.IS_AUTHORIZED,
  },

  PAY_COMPANY_SETTINGS: {
    id: 'PAY_COMPANY_SETTINGS',
    absolute: `/pay/company/:${PARAMS.COMPANY_ID}/configurations/*`,
    relative: `/pay/company/:${PARAMS.COMPANY_ID}/configurations/*`,
    restriction: ACL.CAN_ACCESS_TO_PAY_SETTINGS,
  },

  PAY_COMPANY_SETTINGS_PROVIDERS: {
    id: 'PAY_COMPANY_SETTINGS_PROVIDERS',
    absolute: `/pay/company/:${PARAMS.COMPANY_ID}/configurations/providers`,
    relative: 'providers',
    restriction: ACL.CAN_ACCESS_TO_PAY_SETTINGS,
  },

  PAY_COMPANY_SETTINGS_BENEFICIARIES: {
    id: 'PAY_COMPANY_SETTINGS_BENEFICIARIES',
    absolute: `/pay/company/:${PARAMS.COMPANY_ID}/configurations/beneficiaries`,
    relative: 'beneficiaries',
    restriction: ACL.CAN_ACCESS_TO_PAY_SETTINGS_BENEFICIARIES,
  },

  // ==============================
  // ======== SERVICE
  REDIRECT: {
    id: 'REDIRECT',
    absolute: `/redirect/:${PARAMS.REDIRECT_TO}`,
    relative: `/redirect/:${PARAMS.REDIRECT_TO}`,
    restriction: ACL.IS_AUTHORIZED,
  },
  DEFERRED_TASK: {
    id: 'DEFERRED_TASK',
    absolute: `/deferred_task/:${PARAMS.TASK_ID}`,
    relative: `/deferred_task/:${PARAMS.TASK_ID}`,
    restriction: ACL.IS_AUTHORIZED,
  },
  DOCUMENT_NOT_FOUND: {
    id: 'DOCUMENT_NOT_FOUND',
    absolute: `/company/:${PARAMS.COMPANY_ID}/document_not_found`,
    relative: `/company/:${PARAMS.COMPANY_ID}/document_not_found`,
    restriction: true,
  },
  NOT_SUPPORTED_IPAD: {
    id: 'NOT_SUPPORTED_IPAD',
    absolute: '/ipad_not_supported',
    relative: '/ipad_not_supported',
    restriction: true,
  },
  NOT_SUPPORTED_BROWSER: {
    id: 'NOT_SUPPORTED_BROWSER',
    absolute: '/not_supported_browser',
    relative: '/not_supported_browser',
    restriction: true,
  },
  NOT_SUPPORTED_MOBILE_BROWSER: {
    id: 'NOT_SUPPORTED_MOBILE_BROWSER',
    absolute: `/mobile_browser_not_supported/:${PARAMS.ACTION}`,
    relative: `/mobile_browser_not_supported/:${PARAMS.ACTION}`,
    restriction: true,
  },
  EXPIRED: {
    id: 'EXPIRED',
    absolute: '/expired',
    relative: '/expired',
    restriction: ACL.IS_EXPIRED,
  },
  // ==============================
  // ======== STORYBOOK (component state showcases, dev verification only)
  // Registered for authenticated users in local dev; fully blocked in production.
  // `process.env.REACT_APP_ENV` is inlined at build time by webpack DefinePlugin,
  // so the conditional folds to a constant and prod builds get `restriction: false`,
  // which the auth saga treats as "never allowed" (see /login pattern above).
  STORYBOOK_CHAT: {
    id: 'STORYBOOK_CHAT',
    absolute: '/storybook/chat',
    relative: '/storybook/chat',
    restriction: process.env.REACT_APP_ENV === 'local' ? ACL.IS_AUTHORIZED : false,
  },
  STORYBOOK_CHAT_COLORS: {
    id: 'STORYBOOK_CHAT_COLORS',
    absolute: '/storybook/chat-colors',
    relative: '/storybook/chat-colors',
    restriction: process.env.REACT_APP_ENV === 'local' ? ACL.IS_AUTHORIZED : false,
  },
  PAGE404: {
    id: 'PAGE404',
    absolute: '*',
    relative: '*',
    restriction: true,
  },
};

export type RouteKey = keyof typeof ROUTES_PATH;

// Navigate here, not to generatePath(PAGE404.absolute): that returns '' and re-runs the current route.
export const PAGE404_URL = '/404';

export default ROUTES_PATH;
